import { useState } from "react";
import {
  Title, Group, Button, TextInput, Select, Table, ActionIcon, Stack, Text, Card, Badge, Menu,
} from "@mantine/core";
import { IconPlus, IconSearch, IconDots, IconPencil, IconTrash, IconHistory, IconEye } from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { type Massnahme, MassnahmenStatus, type Locale } from "@iso27001/shared";
import { massnahmenApi } from "../../api/massnahmen.js";
import { MassnahmeFormModal } from "./MassnahmeFormModal.js";
import { AuditHistoryModal } from "../../components/AuditHistoryModal.js";
import { useAuth } from "../../auth/AuthContext.js";
import { formatDate } from "../../i18n/format-date.js";
import { useEnumLabel } from "../../i18n/enum-label.js";
import { useRecordDialog } from "../../components/use-record-dialog.js";
import { rowOpenProps, stopRowOpen, cellBadgeStyles, nowrap } from "../../components/list-table.js";
import { isMassnahmeClosed } from "../../components/record-state.js";
import { massnahmeStatusColor, priorityColor } from "../../components/status-colors.js";
import { useUserName } from "../../components/use-user-name.js";

const isOverdue = (dueDate: number | null, status: string) =>
  !!dueDate && status !== "Erledigt" && status !== "Verworfen" && dueDate * 1000 < Date.now();

export function MassnahmenListPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "editor";
  const qc = useQueryClient();
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const { label } = useEnumLabel();
  const userName = useUserName();

  const statusFilterOptions = [
    { value: "", label: t("common.filter.allStatus") },
    ...MassnahmenStatus.options.map((value) => ({ value, label: label("massnahmeStatus", value) })),
  ];

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [auditId, setAuditId] = useState<number | null>(null);
  const dialog = useRecordDialog<Massnahme>();

  const list = useQuery({
    queryKey: ["massnahmen", { q, status: statusFilter }],
    queryFn: () => massnahmenApi.list({
      q: q || undefined,
      status: (statusFilter || undefined) as MassnahmenStatus | undefined,
      pageSize: 200,
    }),
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => massnahmenApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["massnahmen"] });
      notifications.show({ message: t("massnahmen.list.notify.deleted"), color: "teal" });
    },
  });

  const onDelete = (m: Massnahme) => {
    modals.openConfirmModal({
      title: t("massnahmen.list.confirmDelete.title"),
      children: <Text size="sm">{t("massnahmen.list.confirmDelete.message", { code: m.code })}</Text>,
      labels: { confirm: t("common.action.delete"), cancel: t("common.action.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => removeMut.mutate(m.id),
    });
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>{t("massnahmen.list.title")}</Title>
        {canEdit && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => dialog.open(null)}>
            {t("massnahmen.newMeasure")}
          </Button>
        )}
      </Group>
      <Card withBorder padding="xs">
        <Group>
          <TextInput
            placeholder={t("common.search.placeholder")}
            leftSection={<IconSearch size={14} />}
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            style={{ flex: 1, maxWidth: 320 }}
          />
          <Select
            data={statusFilterOptions}
            value={statusFilter}
            onChange={(val) => setStatusFilter(val ?? "")}
            style={{ width: 180 }}
            placeholder={t("common.filter.allStatus")}
          />
          <Text size="sm" c="dimmed">{list.data ? t("common.entriesCount", { count: list.data.total }) : ""}</Text>
        </Group>
      </Card>
      <Card withBorder padding={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={nowrap}>{t("common.field.code")}</Table.Th>
              <Table.Th>{t("common.field.description")}</Table.Th>
              <Table.Th style={nowrap}>{t("massnahmen.field.responsible")}</Table.Th>
              <Table.Th style={nowrap}>{t("massnahmen.field.dueDate")}</Table.Th>
              <Table.Th style={nowrap}>{t("massnahmen.field.priority")}</Table.Th>
              <Table.Th style={nowrap}>{t("common.field.status")}</Table.Th>
              <Table.Th style={{ width: 48 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {list.data?.items.map((m) => (
              <Table.Tr key={m.id} {...rowOpenProps(() => dialog.open(m))}>
                <Table.Td><Text ff="monospace" size="sm" style={nowrap}>{m.code}</Text></Table.Td>
                <Table.Td>
                  {/* Two lines: these descriptions are sentences, and one line cut most of them mid-clause. */}
                  <Text size="sm" lineClamp={2}>{m.description}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" style={nowrap}>{userName(m.responsibleUserId)}</Text>
                </Table.Td>
                <Table.Td>
                  {m.dueDate ? (
                    <Text size="sm" style={nowrap} c={isOverdue(m.dueDate, m.status) ? "red" : undefined} fw={isOverdue(m.dueDate, m.status) ? 600 : undefined}>
                      {formatDate(m.dueDate, locale)}
                    </Text>
                  ) : null}
                </Table.Td>
                <Table.Td>
                  {m.priority ? (
                    <Badge color={priorityColor[m.priority]} variant="outline" styles={cellBadgeStyles}>{label("priority", m.priority)}</Badge>
                  ) : null}
                </Table.Td>
                <Table.Td>
                  <Badge color={massnahmeStatusColor[m.status]} variant="light" styles={cellBadgeStyles}>{label("massnahmeStatus", m.status)}</Badge>
                </Table.Td>
                <Table.Td {...stopRowOpen}>
                  <Menu position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="subtle" aria-label={t("common.action.actions")}><IconDots size={16} /></ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {canEdit && !isMassnahmeClosed(m.status) ? (
                        <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => dialog.open(m)}>
                          {t("common.action.edit")}
                        </Menu.Item>
                      ) : (
                        <Menu.Item leftSection={<IconEye size={14} />} onClick={() => dialog.open(m)}>
                          {t("common.action.open")}
                        </Menu.Item>
                      )}
                      <Menu.Item leftSection={<IconHistory size={14} />} onClick={() => setAuditId(m.id)}>
                        {t("common.action.history")}
                      </Menu.Item>
                      {canEdit && (
                        <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => onDelete(m)}>
                          {t("common.action.delete")}
                        </Menu.Item>
                      )}
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
            {list.data?.items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text ta="center" c="dimmed" py="md">{t("massnahmen.list.empty")}</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>
      <MassnahmeFormModal key={dialog.key} opened={dialog.opened} onClose={dialog.close} initial={dialog.record} />
      <AuditHistoryModal
        opened={auditId !== null}
        onClose={() => setAuditId(null)}
        queryKey={["massnahmen", "audit", auditId]}
        fetchFn={() => massnahmenApi.audit(auditId!)}
      />
    </Stack>
  );
}
